/**
 * iPhone ProRAW (.dng) files always embed one or more full-resolution JPEG
 * previews inside the TIFF container. Rather than decoding raw Bayer data
 * (huge + slow in the browser), we scan the file bytes for the largest
 * JPEG SOI/EOI marker pair and extract that as a regular .jpg File.
 *
 * Works for every iPhone ProRAW DNG we've tested and most camera DNGs.
 */
export async function convertDngIfNeeded(file: File): Promise<File> {
  const isDng =
    /image\/x-adobe-dng/i.test(file.type) || /\.dng$/i.test(file.name);
  if (!isDng) return file;

  const buf = new Uint8Array(await file.arrayBuffer());

  // Find every JPEG embedded in the DNG (SOI = FF D8 FF, EOI = FF D9)
  // and keep the largest — that's the full-resolution preview.
  let bestStart = -1;
  let bestEnd = -1;
  let bestSize = 0;

  for (let i = 0; i < buf.length - 3; i++) {
    if (buf[i] === 0xff && buf[i + 1] === 0xd8 && buf[i + 2] === 0xff) {
      // Search forward for EOI
      for (let j = i + 2; j < buf.length - 1; j++) {
        if (buf[j] === 0xff && buf[j + 1] === 0xd9) {
          const size = j + 2 - i;
          if (size > bestSize) {
            bestSize = size;
            bestStart = i;
            bestEnd = j + 2;
          }
          i = j + 1; // skip past this JPEG
          break;
        }
      }
    }
  }

  if (bestStart < 0 || bestSize < 1024) {
    throw new Error(
      `Could not read ${file.name}. On iPhone: Settings → Camera → Formats → "High Efficiency" (turns off ProRAW).`
    );
  }

  const jpegBytes = buf.slice(bestStart, bestEnd);
  const newName = file.name.replace(/\.dng$/i, '.jpg');
  return new File([jpegBytes], newName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

export async function convertDngBatch(files: File[]): Promise<File[]> {
  const out: File[] = [];
  for (const f of files) {
    try {
      out.push(await convertDngIfNeeded(f));
    } catch (err) {
      console.error('DNG conversion failed:', err);
      throw err;
    }
  }
  return out;
}
